import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { NOTIFICATION_MARK_READ_CONFLICT_MESSAGE } from '@/constants/messages';
import { markReadSyncNotification } from '@/services/admin/sync-notifications';

/** 既読にする — mark one sync notification read (unread → read). Badge counts unread. */
export const useMarkReadSyncNotificationMutation = () => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteType, id }: { siteType: string; id: number }) =>
      markReadSyncNotification(siteType, id),
    onSuccess: async () => {
      toast.success('既読にしました。');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sync-notifications'] }),
        queryClient.invalidateQueries({ queryKey: ['sync-notifications-count'] }),
      ]);
    },
    onError: error => {
      const status = (error as any)?.response?.status;
      if (status === 401) return;

      // 409 — the notice was already read: a second tab on this inbox, or another operator. Nothing broke,
      // so it gets its own sentence rather than 「エラーが発生しました」, and the list is refetched, because a
      // stale row still offers the 既読にする button that would only earn this toast again. Same handling the
      // 再同期 button beside it already gives BD 24.0.
      if (status === 409) {
        toast.error(
          (error as any)?.response?.data?.message ?? NOTIFICATION_MARK_READ_CONFLICT_MESSAGE
        );
        void queryClient.invalidateQueries({ queryKey: ['sync-notifications'] });
        void queryClient.invalidateQueries({ queryKey: ['sync-notifications-count'] });
        return;
      }

      toast.error('エラーが発生しました。');
    },
  });
};
